import { Permissions } from '@app/core/guard/Permissions';
import {
  PermissionAreaConfig,
  PermissionModuleConfig,
  PermissionScreenConfig,
} from './permission-tree.models';

/**
 * Mapa permissão → tela da tela de grupo de permissão (card 3902).
 *
 * Espelha o menu lateral (`shared/components/side-menu/side-menu.component.ts`): Módulo › Área › Tela. Cada tela traz
 * a claim de menu (`Permissions.Menu*`) e os grupos de permissão de funcionalidade que moram nela, pelo nome do enum
 * do backend sem o sufixo `Permissions` (o `name` de cada grupo de `GET permissions/all`).
 *
 * Regras (detalhe no README da pasta):
 * - Um grupo mora numa tela só. Quando a mesma funcionalidade aparece em mais de um lugar do menu, o grupo é listado
 *   nas telas candidatas, em ordem de preferência, e o builder põe o grupo na PRIMEIRA cuja claim de menu o papel
 *   tem (cai na primeira da lista se nenhuma). As outras telas mostram só "Aparece no menu".
 *   Ex.: a família `DataRoom*` mora em Gerenciamento › Data Room para ADMIN/TENANT e em Performance › Data Room para
 *   quem não tem o item de Gerenciamento.
 * - Grupo do catálogo que não está aqui vai para "Outras (fora do menu)" no fim da árvore. Nada some.
 * - Claim `Menu*` que não está em tela nenhuma também vai para "Outras (fora do menu)".
 *
 * Escolhas de tela principal quando um grupo serve a várias telas:
 * - `LoanOperation` em Controle › Endividamento › Empréstimos (Dashboard, Relatórios, Análise de Performance,
 *   Gestão de Grupos e Próximos Vencimentos usam o mesmo grupo e mostram só o item de menu).
 * - `DerivativeCalculator` em Controle › Derivativos › Operações (Calculadora, Hedge Accounting, MtM e Relatórios
 *   usam o mesmo grupo).
 * - `RiskPanel` em Controle › Gestão de riscos › Painel (Relatórios, Backtesting, Contingência e Painel de Curvas
 *   leem o mesmo grupo).
 * - `Rfq`, `RfqComparison`, `RiskCommittee` e `RiskMandate` em Gestão de riscos › Monitoramento (onde ficam o kanban,
 *   o RFQ, o comitê e o mandato), não em Negociação › RFQ.
 * - `Commercial` em Negociação › Comercial › Deals; `Pipeline` em Negociação › Pipeline de crédito.
 * - `Meeting` e `MeetingVisibility` em Performance › Meetings, com Negociação › Meetings como alternativa.
 * - `Accounting*` e `DocumentPadronization` em Gerenciamento › Padronização de dados contábeis, com Performance ›
 *   Gestão de documentos contábeis como alternativa.
 */

function screen(
  id: string,
  label: string,
  menuPermission?: string,
  permissionGroups: string[] = []
): PermissionScreenConfig {
  return { kind: 'screen', id, label, menuPermission, permissionGroups };
}

function area(id: string, label: string, screens: PermissionScreenConfig[]): PermissionAreaConfig {
  return { kind: 'area', id, label, screens };
}

const MM = Permissions.MenuManagement;
const MC = Permissions.MenuControl;
const MP = Permissions.MenuPerformance;
const MN = Permissions.MenuNegotiation;
const MAI = Permissions.MenuCogAi;

const DATA_ROOM_GROUPS = [
  'DataRoom',
  'DataRoomContactShare',
  'DataRoomEmailDispatch',
  'DataRoomReassignment',
  'DataRoomInbox',
  'DataRoomEconomicGroup',
  'DataRoomMaintenance',
];

const ACCOUNTING_GROUPS = [
  'DocumentPadronization',
  'AccountingValidationOverride',
  'AccountingStatementScale',
  'AccountingDocumentRedo',
  'AccountingSubAccount',
  'AccountingExceptionQueue',
  'AccountingStandardAccountPromotion',
  'AccountingStandardAccountDictionary',
  'AccountingDeParaGoldenCase',
];

const MEETING_GROUPS = ['Meeting', 'MeetingVisibility'];

export const PERMISSION_TREE_CONFIG: PermissionModuleConfig[] = [
  {
    id: 'management',
    label: 'Gerenciamento',
    children: [
      screen('management-company-contacts', 'Contatos da empresa', MM.COMPANY_CONTACTS, [
        'CompanyContact',
      ]),
      area('management-companies', 'Empresas', [
        screen('management-companies-list', 'Gerenciar empresas', MM.COMPANIES, [
          'Company',
          'CompanyAutoRegistration',
        ]),
        screen('management-corporate-structure', 'Societário', MM.CORPORATE_STRUCTURE, [
          'EconomicGroup',
          'EconomicGroupOrgChartImport',
          'CorporateRegistry',
        ]),
        screen(
          'management-client-contract-templates',
          'Modelos de contrato',
          MM.CLIENT_CONTRACT_TEMPLATES,
          ['ClientContract']
        ),
      ]),
      area('management-partners', 'Parceiros', [
        screen(
          'management-partner-contract-template',
          'Modelo de contrato',
          MM.PARTNER_CONTRACT_TEMPLATE,
          ['PartnerContract']
        ),
      ]),
      screen('management-data-room', 'Data Room', MM.DATA_ROOM, DATA_ROOM_GROUPS),
      screen(
        'management-market-projection-curves',
        'Projeções de Mercado',
        MM.MARKET_PROJECTION_CURVES,
        ['MarketProjectionCurve']
      ),
      area('management-cog-ai', 'Cog AI', [
        screen('management-agent-memories', 'Memórias de Agente', MM.AGENT_MEMORIES),
        screen('management-agent-prompts', 'Prompts de Agente', MM.AGENT_PROMPTS),
        screen('management-agent-sessions', 'Sessões de Agente', MM.AGENT_SESSIONS),
        screen('management-ai-costs', 'Custos de IA', MM.AI_COSTS, ['AiCostRecompute']),
        screen('management-ai-models', 'Modelos de IA', MM.AI_MODELS, ['AiModelConfig']),
        screen(
          'management-data-room-automation',
          'Automação do Data Room',
          MM.DATA_ROOM_AUTOMATION,
          ['DataRoomAutomation']
        ),
      ]),
      area('management-support', 'Suporte', [
        screen('management-product-updates', 'Atualizações do Produto', MM.PRODUCT_UPDATES),
        screen('management-jobs-center', 'Central de Rotinas', MM.JOBS_CENTER),
        screen('management-settings', 'Parametrização', MM.SETTINGS),
        screen('management-certification-seals', 'Selos de certificação', MM.CERTIFICATION_SEALS, [
          'CertificationSeal',
        ]),
        screen('management-company-sectors', 'Setores', MM.COMPANY_SECTORS, ['CompanySector']),
      ]),
      area('management-communication', 'Comunicação', [
        screen('management-send-notifications', 'Enviar notificações', MM.SEND_NOTIFICATIONS),
        screen('management-monthly-dispatch', 'Envio Mensal', MM.MONTHLY_DISPATCH, [
          'PositionReportDispatch',
        ]),
        screen(
          'management-weekly-market-report',
          'Relatório Semanal de Mercado',
          MM.WEEKLY_MARKET_REPORT,
          ['MarketWeeklyReport']
        ),
        screen('management-feedbacks', 'Feedbacks', MM.FEEDBACKS),
      ]),
      area('management-analysis', 'Análises', [
        screen(
          'management-risk-analysis-rules',
          'Regras de Análise de Risco',
          MM.RISK_ANALYSIS_RULES
        ),
        screen('management-document-analysis', 'Análise de Documentos', MM.DOCUMENT_ANALYSIS),
        screen(
          'management-accounting-standardization',
          'Padronização de dados contábeis',
          MM.ACCOUNTING_STANDARDIZATION,
          ACCOUNTING_GROUPS
        ),
      ]),
      area('management-access', 'Acessos', [
        screen('management-tenants', 'Tenants', MM.TENANTS, ['Tenant']),
        screen('management-users', 'Usuários', MM.USERS, ['User', 'UserImpersonation']),
        screen('management-permission-groups', 'Grupos de Permissões', MM.PERMISSION_GROUPS, [
          'PermissionGroup',
        ]),
      ]),
      area('management-registers', 'Cadastros', [
        screen(
          'management-financial-institutions',
          'Instituições Financeiras',
          MM.FINANCIAL_INSTITUTIONS,
          ['FinancialInstitution', 'FinancialInstitutionFundingOrigin']
        ),
        screen(
          'management-credit-limit-types',
          'Tipos de Limite de Crédito',
          MM.CREDIT_LIMIT_TYPES
        ),
      ]),
      area('management-billing', 'Faturamento', [
        screen('management-billing-loans', 'Empréstimos', MM.BILLING, ['ActiveManagementCalendar']),
      ]),
    ],
  },
  {
    id: 'control',
    label: 'Controle',
    children: [
      area('control-debt', 'Endividamento', [
        screen('control-loans', 'Empréstimos', MC.LOANS, ['LoanOperation']),
        screen('control-loan-dashboard', 'Dashboard', MC.LOAN_DASHBOARD),
        screen('control-loan-reports', 'Relatórios', MC.LOAN_REPORTS),
        screen('control-debt-reports', 'Relatórios v2', MC.DEBT_REPORTS),
        screen('control-loan-performance', 'Análise de Performance', MC.LOAN_PERFORMANCE),
        screen('control-collaterals', 'Garantias', MC.COLLATERALS, ['Guarantee']),
        screen('control-loan-groups', 'Gestão de Grupos', MC.LOAN_GROUPS),
      ]),
      area('control-derivatives', 'Derivativos', [
        screen('control-derivative-operations', 'Operações', MC.DERIVATIVE_OPERATIONS, [
          'DerivativeCalculator',
          'DerivativeAttachmentType',
          'DerivativeRecalculateOperations',
        ]),
        screen('control-derivative-calculator', 'Calculadora', MC.DERIVATIVE_CALCULATOR),
        screen('control-hedge-accounting', 'Hedge Accounting', MC.HEDGE_ACCOUNTING),
        screen('control-mark-to-market', 'Marcação a Mercado', MC.MARK_TO_MARKET),
        screen('control-spread-intelligence', 'Spread Intelligence', MC.SPREAD_INTELLIGENCE, [
          'SpreadIntelligence',
        ]),
        screen('control-derivative-reports', 'Relatórios', MC.DERIVATIVE_REPORTS),
      ]),
      area('control-risk', 'Gestão de riscos', [
        screen('control-risk-panel', 'Painel', MC.RISK_PANEL, ['RiskPanel']),
        screen('control-market-monitor', 'Mercado', MC.MARKET_MONITOR, ['MarketMonitor']),
        screen('control-risk-reports', 'Relatórios', MC.RISK_REPORTS),
        screen('control-risk-monitoring', 'Monitoramento', MC.RISK_MONITORING, [
          'RiskMonitoring',
          'Rfq',
          'RfqComparison',
          'RiskCommittee',
          'RiskMandate',
        ]),
        screen('control-backtesting', 'Backtesting', MC.BACKTESTING),
        screen('control-contingency', 'Contingência', MC.CONTINGENCY, ['RiskContingency']),
        screen('control-curves-panel', 'Painel de Curvas', MC.CURVES_PANEL),
        screen(
          'control-sensitivity-analysis',
          'Análise de Sensibilidade',
          MC.SENSITIVITY_ANALYSIS,
          ['SensitivityAnalysis']
        ),
      ]),
      area('control-comex', 'Importação', [
        screen('control-comex-operations', 'Operações', MC.COMEX_OPERATIONS, ['Comex']),
        screen('control-comex-kanban', 'Kanban', MC.COMEX_KANBAN),
      ]),
      area('control-fx', 'Câmbio', [
        screen('control-fx-operations', 'Operações', MC.FX_OPERATIONS, [
          'ForeignExchangeOperations',
        ]),
        screen('control-fx-effective-rates', 'Efetivo', MC.FX_EFFECTIVE_RATES, [
          'EffectiveExchangeRates',
        ]),
      ]),
      screen('control-investments', 'Investimentos', MC.INVESTMENTS),
      screen('control-upcoming-maturities', 'Próximos Vencimentos', MC.UPCOMING_MATURITIES),
    ],
  },
  {
    id: 'performance',
    label: 'Performance',
    children: [
      screen('performance-dashboard', 'Dashboard', MP.DASHBOARD),
      screen('performance-economic-analysis', 'Análise Econômica', MP.ECONOMIC_ANALYSIS, [
        'Dfp',
        'DfpFinancialReading',
        'DfpOriginalStatement',
        'PartnerIncomeTax',
      ]),
      screen('performance-meetings', 'Meetings', MP.MEETINGS, MEETING_GROUPS),
      screen('performance-credit-score', 'Análise Score', MP.CREDIT_SCORE, ['CreditScore']),
      screen('performance-financial-modeling', 'Modelagem Financeira', MP.FINANCIAL_MODELING, [
        'DfpAssumption',
        'DfpComments',
      ]),
      screen('performance-business-case', 'Business Case', MP.BUSINESS_CASE, [
        'BusinessCase',
        'BackOfficeBusinessCase',
      ]),
      screen(
        'performance-accounting-documents',
        'Gestão de documentos contábeis',
        MP.ACCOUNTING_DOCUMENTS,
        ACCOUNTING_GROUPS
      ),
      screen('performance-data-room', 'Data Room', MP.DATA_ROOM, DATA_ROOM_GROUPS),
      area('performance-project-finance', 'Project Finance', [
        screen('performance-pf-projects', 'Projetos', MP.PROJECT_FINANCE_PROJECTS),
        screen('performance-pf-scope', 'Escopo', MP.PROJECT_FINANCE_SCOPE),
        screen(
          'performance-pf-schedule',
          'Cronograma Físico Financeiro',
          MP.PROJECT_FINANCE_SCHEDULE
        ),
        screen('performance-pf-cash-flow', 'Fluxo de Caixa', MP.PROJECT_FINANCE_CASH_FLOW),
      ]),
    ],
  },
  {
    id: 'negotiation',
    label: 'Negociação',
    children: [
      area('negotiation-commercial', 'Comercial', [
        screen('negotiation-partner-home', 'Início do parceiro', MN.PARTNER_HOME),
        screen('negotiation-register-deal', 'Registrar Deal', MN.REGISTER_DEAL),
        screen('negotiation-deals', 'Deals', MN.DEALS, ['Commercial']),
        screen('negotiation-credit-committee', 'Comitê de crédito', MN.CREDIT_COMMITTEE, [
          'PartnerDealCommittee',
        ]),
        screen('negotiation-meetings', 'Meetings', MN.MEETINGS, MEETING_GROUPS),
        screen('negotiation-partner-calculator', 'Calculadora', MN.PARTNER_CALCULATOR),
        screen('negotiation-partners', 'Parceiros', MN.PARTNERS, ['Partner']),
        screen('negotiation-partner-users', 'Usuários do parceiro', MN.PARTNER_USERS),
        screen('negotiation-referral', 'Indique e ganhe', MN.REFERRAL),
        screen('negotiation-my-operation', 'Minha operação', MN.MY_OPERATION),
      ]),
      screen('negotiation-credit-pipeline', 'Pipeline de crédito', MN.CREDIT_PIPELINE, [
        'Pipeline',
      ]),
      screen('negotiation-pipeline-dashboard', 'Dashboard de Pipelines', MN.PIPELINE_DASHBOARD),
      screen('negotiation-activity-feed', 'Feed de Atividades', MN.ACTIVITY_FEED),
      screen('negotiation-task-list', 'Lista de Tarefas', MN.TASK_LIST),
      screen('negotiation-rfq', 'RFQ', MN.RFQ),
      area('negotiation-bank-relationship', 'Relacionamento Bancário', [
        screen('negotiation-bank-accounts', 'Contas Bancárias', MN.BANK_ACCOUNTS, [
          'BankRelationship',
        ]),
        screen('negotiation-bank-contacts', 'Contatos Bancários', MN.BANK_CONTACTS),
      ]),
      screen('negotiation-data-room', 'Data Room', MN.DATA_ROOM, DATA_ROOM_GROUPS),
    ],
  },
  {
    id: 'cog-ai',
    label: 'Cog IA',
    children: [screen('cog-ai-chat', 'Cog AI', MAI.COG_AI), screen('cog-ai-mcp', 'MCP', MAI.MCP)],
  },
];

/** Prefixo dos grupos de visibilidade de menu no catálogo (`MenuControl`, `MenuManagement`...). */
export const MENU_GROUP_PREFIX = 'Menu';

/** Id e rótulo do módulo que junta o que não está em tela nenhuma. */
export const OTHER_MODULE_ID = 'other';
export const OTHER_MODULE_LABEL = 'Outras (fora do menu)';
export const UNMAPPED_MENU_SCREEN_ID = 'other-unmapped-menu';
export const UNMAPPED_MENU_SCREEN_LABEL = 'Itens de menu sem tela mapeada';
